import {Component, computed, effect, model, inject} from '@angular/core';
import {ReactiveFormsModule, FormGroup} from '@angular/forms';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {Panel} from 'primeng/panel';
import {ProjectStepSaveButtonComponent} from '../../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {NgClass} from '@angular/common';
import {TableModule} from 'primeng/table';
import {TranslocoPipe} from '@jsverse/transloco';
import {AppCurrencyPipe, IntegrationOtherFeeResponse} from '@frontend/shared';
import {PanelHeaderComponent} from '../../../../../../../../shared/components/header/panel-header/panel-header.component';
import {FormControlsConfig} from '../../../step-8-1-proposal-parameter/components/abstract-form-directive';
import {CommentModalButtonComponent} from '../../../../../../../../shared/components/dialog/comment-modal-button/comment-modal-button-reactive/comment-modal-button.component';
import {IntegrationOtherFeesContainer} from './integration-other-fees.types';
import {toSignal} from '@angular/core/rxjs-interop';
import {DirectusTranslation} from '@frontend/directus';
import {SumFees} from '../step-8-2-3-rfq-preview-price-list.types';
import {FormFieldNumberComponent} from '../../../../../../../../shared/components/inputs/form-field/form-field-number/form-field-number.component';
import {AbstractFormArrayDirective} from '../../../step-8-1-proposal-parameter/components/abstract-form-array-directive';
import {startWith} from 'rxjs';
import {RfqFeeCategoryService} from '../../../../../../../../shared/services/rfq-fee-category.service';

interface FeeRowVM {
  data: IntegrationOtherFeeResponse;
  totalAcrossScope: number;
}

@Component({
  selector: 'app-integration-other-fees-module',
  imports: [
    ReactiveFormsModule,
    Panel,
    ProjectStepSaveButtonComponent,
    NgClass,
    PanelHeaderComponent,
    TableModule,
    TranslocoPipe,
    CommentModalButtonComponent,
    AppCurrencyPipe,
    TranslateDirectusPipe,
    FormFieldNumberComponent,
  ],
  templateUrl: './integration-other-fees.component.html',
  providers: [TranslateDirectusPipe],
})
export class IntegrationOtherFeesComponent extends AbstractFormArrayDirective<
  IntegrationOtherFeesContainer,
  IntegrationOtherFeeResponse
> {
  protected readonly feeCategories = inject(RfqFeeCategoryService);
  private static readonly FORM_CONTROLS: FormControlsConfig<IntegrationOtherFeesContainer> = {
    locale: [''],
    currencyCode: [''],
    scopeYears: [[]],
    data: [[]],
  };

  readonly integrationOtherFeeOptions = toSignal(
    this.directusService.loadDirectusDropdown('integration_other_fees'),
    {
      initialValue: [],
    },
  );

  private formValue = toSignal(this.theForm.valueChanges.pipe(startWith(this.theForm.value)));

  readonly feeRows = computed<FeeRowVM[]>(() => {
    this.formValue();
    const current = this.inputObject();
    const formData = this.theForm.get('data')?.value ?? [];
    if (!current?.data?.length) return [];

    return current.data.map((item, index) => {
      const formRow = formData[index];
      const formYearlyFees = formRow?.yearlyItemFees ?? item.yearlyItemFees;

      return {
        data: {
          directusDropdownOtherFeeUuid: item.directusDropdownOtherFeeUuid,
          yearlyItemFees: formYearlyFees,
          comment: formRow?.comment ?? item.comment,
        },
        totalAcrossScope: formYearlyFees.reduce(
          (sum: number, fee: any) => sum + (Number(fee.feePerUnit) || 0),
          0,
        ),
      } as FeeRowVM;
    });
  });

  readonly columnTotalsPerYear = computed<number[]>(() =>
    this.inputObject().scopeYears.map((_, yearIndex) =>
      this.feeRows().reduce(
        (sum, row) => sum + (Number(row.data.yearlyItemFees[yearIndex]?.feePerUnit) || 0),
        0,
      ),
    ),
  );

  readonly grandTotalAllYears = computed<number>(() =>
    this.feeRows().reduce((sum, row) => sum + row.totalAcrossScope, 0),
  );

  readonly sumFees = computed<SumFees>(() => {
    const columnTotalsPerYear = this.columnTotalsPerYear();
    const grandTotalAllYears = this.grandTotalAllYears();
    return {
      columnTotalsPerYear: columnTotalsPerYear,
      grandTotalAllYears: grandTotalAllYears,
    } as SumFees;
  });

  readonly sumFeesModel = model<SumFees>({} as SumFees);

  constructor() {
    super(IntegrationOtherFeesComponent.FORM_CONTROLS);

    effect(() => {
      this.sumFeesModel.set(this.sumFees());
    });
  }

  protected createRowFormGroup(item: IntegrationOtherFeeResponse): FormGroup {
    return this.fb.group({
      directusDropdownOtherFeeUuid: [item.directusDropdownOtherFeeUuid],
      yearlyItemFees: this.fb.array(
        item.yearlyItemFees.map((fee) =>
          this.fb.group({
            index: [fee.index],
            feePerUnit: [fee.feePerUnit],
          }),
        ),
      ),
      comment: [item.comment],
    });
  }

  protected getIntegrationOtherFee(uuid: string): DirectusTranslation[] {
    const option = this.integrationOtherFeeOptions().find((x) => x.id === uuid);

    return option?.translations ?? [];
  }
}
